/*
 * Copyright (c) 2024 Mahon Media LLC.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to read
 * the Software only. Permissions is hereby NOT GRANTED to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import Cookies from "js-cookie";
import {useHistory} from "react-router-dom/cjs/react-router-dom";
import React, {useEffect, useState} from "react";
import {
  Box,
  Button, CircularProgress, Grid, IconButton, LinearProgress, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import {Modal} from "react-overlays";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import {useNotification} from "../contexts/NotificationContext";
import {MAIN_ERROR_MESSAGES} from "../constants/errorMessages";
import moment from "moment";
import 'moment-timezone';
import FormLabel from "@mui/material/FormLabel";
import {Add as AddIcon, Remove as RemoveIcon} from '@mui/icons-material';
import SockJS from 'sockjs-client';
import {Client} from '@stomp/stompjs';
import { getBackendBaseUrl } from "../utils/envUtils";

function CrewPopulated() {
  const {setIsOpen, setMessage, setIsFailed} = useNotification();
  const [loading, setLoading] = useState(false);
  const [maxProgress, setMaxProgress] = useState(0);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [validating, setValidating] = useState(false);
  const [validatingText, setValidatingText] = useState();
  const [progressText, setProgressText] = useState();
  const [showPopulateModal, setShowPopulateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  moment.tz.setDefault('America/Los_Angeles');
  const [start, setStart] = useState(new Date());
  const [rooms, setRooms] = useState('reservations/new');
  const [reservationsData, setReservationsData] = useState([]);
  const [selectedReservation, setSelectedReservation] = useState([]);
  const [roomTypesAndAmounts, setRoomTypesAndAmounts] = useState([{room: '', amount: 1}]);
  const [created, setCreated] = useState(null);
  const [stay, setStay] = useState(2);
  const [errors, setErrors] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [month, setMonth] = useState();
  const [rtype, setRType] = useState();
  const [room, setRoom] = useState();
  const [firstname, setFirstname] = useState();
  const [lastname, setLastname] = useState();
  const [amount, setAmount] = useState(null);
  const token = Cookies.get("crew_token");
  const ohip = Cookies.get('jwtToken');
  const history = useHistory();
  useEffect(() => {
    if (!ohip || !token) {
      history.push("/");
      setIsOpen(true);
      setIsFailed(true);
      setMessage(MAIN_ERROR_MESSAGES.tokenExpired);
    }
    //reservations(moment(new Date()).format('YYYY-MM-DD')).then((response) => console.log(response));
  }, []);

  const showPopulate = () => {
    setShowPopulateModal(true);
    handleSelectRoomType().then(r => r);
  };

  const handleCancel = () => {
    setShowPopulateModal(false);
    setCreated([]);
    setErrors([]);
    setRoomTypesAndAmounts([{room: '', amount: 1}]);
    setLoading(false);
    setProgressText(null);
  };

  const handleMonthChange = (event) => {
    setMonth(event.target.value);
  };

  const handleRTypeChange = (event) => {
    setRType(event.target.value);
  };

  const renderBackdrop = (props) => {
    <div className="backdrop" {...props} />;
  };

  const reservations = async (date, types) => {
    const pattern = /^\d{4}-\d{2}-\d{2}$/;
    let formatted;
    if (pattern.test(date)) {
      formatted = date;
    } else {
      formatted = new Date(
        `${date.getFullYear()}-${String(101 + date.getMonth()).substr(1)}-${String(100 + date.getDate()).substr(1)}`
      ).toISOString().substring(0, 10);
    }
    const token = Cookies.get("crew_token");
    setLoading(true);
    try {
      host = getBackendBaseUrl();
      const response = await fetch(
        `${host}/${types}?date=${formatted}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReservationsData(data);
        setLoading(false);
      } else {
        throw new Error(response.statusMessage);
      }
    } catch (err) {
      setIsOpen(true);
      setIsFailed(true);
      setMessage(err.message);
      setLoading(false);
    }
  };

  const populate = (e) => {
    const host = getBackendBaseUrl();
    e.preventDefault();
    setCreated(null);
    setErrors([]);
    const totalPairs = roomTypesAndAmounts.length;
    setCreated([]);
    const allCreated = [];
    const token = Cookies.get('crew_token');
    let validatedPairs = 0;
    let completedPairs = 0;
    const validationStatus = {};
    roomTypesAndAmounts.map((pair) => {
      validationStatus[pair.room] = false;
      setValidating(true);
      setValidatingText(`Checking availability`);
      const socket = new SockJS(`${host}/sockets`);
      const stompClient = new Client({
        webSocketFactory: () => socket,
        debug: (str) => {
          console.log(str);
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        connectHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
      stompClient.onConnect = (frame) => {
        console.log('Connected: ' + frame);
        stompClient.subscribe('/topic/max', (message) => {
          setValidatingText(null);
          setValidating(false);
          setLoading(true);
          setMaxProgress(message.body);
          setProgressText('Populating reservations');
        });
        stompClient.subscribe('/topic/population', (message) => {
          setCurrentProgress(message.body);
        });
        stompClient.subscribe('/topic/result', (message) => {
          console.log(message.body);
          const {room, size} = JSON.parse(message.body);
          if (!allCreated.some(item => item.room === room)) {
            allCreated.push({room: room, created: size});
            validationStatus[pair.room] = true;
            validatedPairs++;
            checkCompletion();
          }
        });
        stompClient.subscribe('/topic/errors', (message) => {
          const errors = JSON.parse(message.body).errors;
          const formattedErrors = errors.map((error) => `${error.message}`);
          setErrors((prevErrors) => {
            const unique = new Set(prevErrors.concat(formattedErrors));
            return Array.from(unique);
          });
          validationStatus[pair.room] = true;
          validatedPairs++;
          checkCompletion();
        });

        function checkCompletion() {
          const allValidated = Object.values(validationStatus).every(status => status === true);
          if (allValidated) {
            setValidating(false);
            setValidatingText(null);
            stompClient.deactivate();
          }
        }

        stompClient.publish({
          destination: '/opera/start-population',
          body: JSON.stringify({
            month: Number.parseInt(month),
            room: pair.room,
            amount: pair.amount,
            lengthOfStay: Number(stay)
          }),
        });
      };
      stompClient.onDisconnect = () => {
        completedPairs++;
        setCreated(allCreated);
        if (completedPairs === totalPairs) {
          const unique = new Set(allCreated);
          setCreated(Array.from(unique));
          setLoading(false);
          setProgressText(null);
        }
      };
      stompClient.activate();
    });
  };


  const handleUpdateReservation = (reservation) => {
    setShowUpdateModal(true);
    setSelectedReservation(reservation);
    setFirstname(reservation.firstname);
    setLastname(reservation.lastname);
    handleSelectRoomType().then(r => r);
  };

  const closeUpdate = () => {
    setShowUpdateModal(false);
    setFirstname(null);
    setLastname(null);
  };

  const fresh = async () => {
    const token = Cookies.get("crew_token");
    setLoading(true);
    const formattedDate = new Date(
      `${start.getFullYear()
      }-${String(101 + start.getMonth()).substr(1)
      }-${String(100 + start.getDate()).substr(1)
      }`
    ).toISOString().substring(0, 10);
    try {
      const host = getBackendBaseUrl();
      const response = await fetch(
        `${host}/reservations?date=${formattedDate}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRooms('reservations');
        setReservationsData(data);
        setLoading(false);
      } else {
        throw new Error(response.statusMessage);
      }
    } catch (err) {
      setIsOpen(true);
      setIsFailed(true);
      setMessage(err.message);
      setLoading(false);
    }
  };

  const updateReservations = async () => {
    const token = Cookies.get("crew_token");
    setLoading(true);
    let type;
    if (rtype === undefined) {
      type = selectedReservation.room;
    } else {
      type = rtype;
    }
    try {
      const host = getBackendBaseUrl();
      const response = await fetch(
        `${host}/reservations`,
        {
          method: 'PUT',
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            guest: {
              firstname: firstname,
              lastname: lastname
            },
            room: type,
            identifier: selectedReservation.id,
            start: selectedReservation.arrival,
            end: selectedReservation.departure
          }),
        }
      );
      if (response.ok) {
        setLoading(false);
        setIsOpen(true);
        setIsFailed(false);
        setMessage(`${selectedReservation.id} was updated`);
        closeUpdate();
        await fresh();
      } else {
        const data = await response.json();
        throw new Error(data.errors[0].message);
      }
    } catch (err) {
      setIsOpen(true);
      setIsFailed(true);
      setMessage(err.message);
      setLoading(false);
    }
  };

  const handleSelectRoomType = async () => {
    const host = getBackendBaseUrl();
    const ohip = Cookies.get("jwtToken");
    try {
      const profileResponse = await fetch(
        `${host}/api/v1/room-types?env=sandbox`,
        {
          headers: {
            Authorization: `Bearer ${ohip}`,
          },
        }
      );
      if (profileResponse.status === 401) {
        throw new Error(response.status);
      }
      if (!profileResponse.ok) {
        throw new Error("Network response was not ok");
      }
      const options = await profileResponse.json();
      const types = options.map(item => item.code);
      setRoomTypes(types);
    } catch (error) {
      const errorMessage =
        MAIN_ERROR_MESSAGES[error.message] || MAIN_ERROR_MESSAGES.default;
      setIsOpen(true);
      setIsFailed(true);
      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const CustomInput = React.forwardRef(({value, onClick}, ref) => (
    <input
      ref={ref}
      value={value}
      onClick={onClick}
      style={{height: '40px', width: '200px', marginTop: '10px', textAlign: "center"}}
    />
  ));

  const handleRoomsChange = (e) => {
    if (e.target.value === undefined) {
      reservations(start, rooms);
    } else {
      setRooms(e.target.value);
      reservations(start, e.target.value).then(r => r);
    }
  };

  const handleRoomChange = (value, index) => {
    const updatedRoomTypesAndAmounts = [...roomTypesAndAmounts];
    updatedRoomTypesAndAmounts[index].room = value;
    setRoomTypesAndAmounts(updatedRoomTypesAndAmounts);
  };

  const handleQuantityChange = (value, index) => {
    if (value < 1) {
      value = 1;
    }
    const updatedRoomTypesAndAmounts = [...roomTypesAndAmounts];
    updatedRoomTypesAndAmounts[index].amount = value;
    setRoomTypesAndAmounts(updatedRoomTypesAndAmounts);
  };

  const addRoomTypeAndAmount = () => {
    setRoomTypesAndAmounts([...roomTypesAndAmounts, {room: '', amount: 1}]);
  };

  return <>
    <Paper
      elevation={3}
      style={{
        padding: "20px",
        fontFamily: "Monaco, sans-serif",
        fontSize: "14px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        marginBottom: "40px"
      }}
    >
      <h2>Crew Rooms Manager</h2>
      <div style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%"
      }}>
        <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
          <Select
            required
            value={rooms}
            onChange={handleRoomsChange}
            sx={{
              mt: '10px',
              marginLeft: '10px',
              marginRight: '20px',
              height: '45px',
              width: '200px',
            }}
          >
            <MenuItem value="reservations/new">New Rooms</MenuItem>
            <MenuItem value="reservations/updated">Updated Rooms</MenuItem>
            <MenuItem value="reservations">All Rooms</MenuItem>
          </Select>
          <DatePicker
            customInput={<CustomInput/>}
            selected={start}
            onChange={(date) => {
              const formattedDate = new Date(
                `${date.getFullYear()}-${String(101 + date.getMonth()).substr(1)}-${String(100 + date.getDate()).substr(1)}`
              ).toISOString().substring(0, 10);
              console.log(formattedDate);
              console.log(rooms);
              reservations(formattedDate, rooms).then(r => r);
              console.log(date);
              setStart(date);
            }}
            popperPlacement="bottom-start"
          />
        </div>
        <div style={{marginLeft: "auto"}}>
          <Button onClick={() => showPopulate()}>
            Create Room Reservations
          </Button>
          <Modal
            className="modal-populate-reservations"
            show={showPopulateModal}
            onHide={handleCancel}
            renderBackdrop={renderBackdrop}
          >
            <div>
              <div className="modal-header">
                <div className="modal-title">Create New Room Reservations</div>
                <div>
                  <Button onClick={handleCancel}>X</Button>
                </div>
              </div>
              <form onSubmit={populate}>
                <FormControl fullWidth required>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6} mdOffset={3} sx={{marginLeft: '20px'}}>
                      <Box sx={{marginTop: '10px'}}>
                        <Grid container spacing={2} allignItems="center">
                          <Grid item xs={6}>
                            <FormLabel
                              id="month-label"
                              sx={{
                                "& :focused": {
                                  color: "#00000099",
                                }
                              }}
                            >
                              Select month
                            </FormLabel>
                            <Select
                              labelId="month-label"
                              fullWidth
                              required
                              value={month}
                              onChange={handleMonthChange}
                              sx={{mt: '10px'}}
                            >
                              <MenuItem value="1">January</MenuItem>
                              <MenuItem value="2">February</MenuItem>
                              <MenuItem value="3">March</MenuItem>
                              <MenuItem value="4">April</MenuItem>
                              <MenuItem value="5">May</MenuItem>
                              <MenuItem value="6">June</MenuItem>
                              <MenuItem value="7">July</MenuItem>
                              <MenuItem value="8">August</MenuItem>
                              <MenuItem value="9">September</MenuItem>
                              <MenuItem value="10">October</MenuItem>
                              <MenuItem value="11">November</MenuItem>
                              <MenuItem value="12">December</MenuItem>
                            </Select>
                          </Grid>
                          <Grid item xs={6}>
                            <FormLabel
                              id="stay-label"
                              sx={{
                                "& :focused": {
                                  color: "#00000099",
                                }
                              }}
                            >
                              Length of Stay
                            </FormLabel>
                            <Box sx={{width: '70%', display: 'flex', alignItems: 'center', marginTop: '15px'}}>
                              <IconButton
                                aria-label="decrement"
                                onClick={() => setStay(stay - 1)}
                                disabled={stay <= 1}
                              >
                                <RemoveIcon/>
                              </IconButton>
                              <Typography variant="body1" sx={{mx: 1}}>
                                {stay}
                              </Typography>
                              <IconButton
                                aria-label="increment"
                                onClick={() => setStay(stay + 1)}
                              >
                                <AddIcon/>
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                      <div style={{position: 'relative'}}>
                        <div style={{position: 'absolute', left: '450px', bottom: '-190px'}}>
                          <Paper style={{
                            backgroundColor: '#d3d3d3',
                            padding: '10px',
                            borderRadius: '10px',
                            marginBottom: '20px',
                            width: '350px',
                            flexDirection: 'column',
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                            }}>
                              <pre style={{marginRight: '230px', fontSize: 12}}>
                                {`
                                "amountBeforeTax": "165.01"
                                "roomType": "GKHA"
                                "ratePlanCode": "CAIRFR"
                                "marketCode": "CONTRACT"
                                "marketCodeDescription": "CONTRACT"
                                "sourceCode": "CONTRACT"
                                "sourceCodeDescription: "CONTRACT"
                                "paymentMethod": "CK"
                                "guaranteeCode": "6PM"
                                "taxType": {
                                 "code": "00",
                                 "description": "Regular Guest - Pays All Taxes"
                                }
                                `}
                              </pre>
                            </div>
                          </Paper>
                        </div>
                      </div>
                      <Box sx={{marginTop: '10px'}}>
                        {roomTypesAndAmounts.map((item, index) => (
                          <Grid container spacing={2} allignItems="center" key={index}>
                            <Grid item xs={6}>
                              <FormLabel
                                id="month-label"
                                sx={{
                                  "& :focused": {
                                    color: "#00000099",
                                  }
                                }}
                              >
                                Room Type
                              </FormLabel>
                              <Select
                                labelId="month-label"
                                fullWidth
                                required
                                value={item.room}
                                onChange={(e) => handleRoomChange(e.target.value, index)}
                                sx={{mt: '10px'}}
                              >
                                {roomTypes.map((type, typeIndex) => (
                                  <MenuItem value={type} key={typeIndex}>
                                    {type}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Grid>
                            <Grid item xs={4}>
                              <Box sx={{marginTop: '20px'}}>
                                <FormLabel
                                  id="quantity-label"
                                  sx={{
                                    "& :focused": {
                                      color: "#00000099",
                                    }
                                  }}
                                >
                                  Quantity
                                </FormLabel>
                              </Box>
                              <Box sx={{width: '70%', display: 'flex', alignItems: 'center', marginTop: '15px'}}>
                                <IconButton
                                  aria-label="decrement"
                                  onClick={() => handleQuantityChange(item.amount - 1, index)}
                                  disabled={item.amount <= 1}
                                >
                                  <RemoveIcon/>
                                </IconButton>
                                <Typography variant="body1" sx={{mx: 1}}>
                                  {item.amount}
                                </Typography>
                                <IconButton
                                  aria-label="increment"
                                  onClick={() => handleQuantityChange(item.amount + 1, index)}
                                >
                                  <AddIcon/>
                                </IconButton>
                              </Box>
                            </Grid>
                            {index === roomTypesAndAmounts.length - 1 && (
                              <Grid
                                item xs={2}
                                style={{textAlign: 'right', marginTop: '40px'}}
                              >
                                <Button
                                  style={{
                                    fontSize: '24px',
                                    backgroundColor: 'white',
                                    color: 'blue',
                                    padding: '10px 20px',
                                  }} onClick={() => addRoomTypeAndAmount()}
                                >+</Button>
                              </Grid>
                            )}
                          </Grid>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </FormControl>
                <FormControl sx={
                  {
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    flexDirection: "column",
                  }
                }>
                  {validating && <CircularProgress sx={{mt: 3, marginRight: 60}}/>}
                  <p style={{marginRight: 450}}>{validatingText}</p>
                </FormControl>
                {loading && <LinearProgress
                  variant="determinate"
                  value={currentProgress * 100 / maxProgress}
                  style={{marginTop: 60}}
                >
                </LinearProgress>
                }
                <p>{progressText}</p>
                <div className="modal-desc">
                  {created && created.length > 0 ? (
                    <>
                      <h3>Created reservations:</h3>
                      {created.map((reservation, index) => (
                        <div key={index}>
                          <p>Room: {reservation.room}</p>
                          <p>Created: {reservation.created}</p>
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
                <div className="modal-desc">
                  {errors.length !== 0 ? (
                    <>
                      <h3>Errors occurred:</h3>
                      {errors.map((error, index) => (
                        <div key={index} className="error-message">
                          {error}
                        </div>
                      ))}
                    </>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button className="secondary-button" onClick={handleCancel}>
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    variant={"contained"}
                    sx={{
                      textTransform: "none",
                    }}
                    color="primary"
                  >
                    Create New Room Reservations
                  </Button>
                </div>
              </form>
            </div>
          </Modal>
        </div>
      </div>
    </Paper>
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Arrival Date</TableCell>
              <TableCell>Check Out Date</TableCell>
              <TableCell>Room Type</TableCell>
              <TableCell>Firstname</TableCell>
              <TableCell>Lastname</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reservationsData.map((reservation, index) => (
              <TableRow key={index}>
                <TableCell>{reservation.id}</TableCell>
                <TableCell>{reservation.arrival}</TableCell>
                <TableCell>{reservation.departure}</TableCell>
                <TableCell>{reservation.room}</TableCell>
                <TableCell>{reservation.firstname}</TableCell>
                <TableCell>{reservation.lastname}</TableCell>
                <TableCell>
                  <Button
                    color="primary"
                    sx={{
                      display: 'block',
                      margin: 'auto',
                      width: 'fit-content',
                    }}
                    onClick={() => handleUpdateReservation(reservation)}
                  >
                    Update
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        className="update-reservation-modal"
        show={showUpdateModal}
        onHide={closeUpdate}
        renderBackdrop={renderBackdrop}
      >
        <div>
          <div className="modal-header">
            <div className="modal-title">Update Reservation</div>
            <div>
              <Button onClick={closeUpdate}>X</Button>
            </div>
          </div>
          <FormControl sx={{
            display: 'flex'
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} mdOffset={3}
                    sx={{marginLeft: '20px', marginTop: '20px'}}>
                <table className="synced">
                  <thead>
                  <tr>
                    <th>Reservation</th>
                    <th>Room</th>
                    <th>Arrival</th>
                    <th>Departure</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr key={1}>
                    <td>{selectedReservation.id ? selectedReservation.id : "None"}</td>
                    <td>{selectedReservation.room ? selectedReservation.room : "None"}</td>
                    <td>{selectedReservation.arrival ? selectedReservation.arrival : "None"}</td>
                    <td>{selectedReservation.departure ? selectedReservation.departure : "None"}</td>
                  </tr>
                  </tbody>
                </table>
                <FormLabel
                  id="lastname-label"
                  sx={{
                    "& :focused": {
                      color: "#00000099",
                    }
                  }}
                >
                  Firstname
                </FormLabel>
                <TextField
                  sx={{
                    mt: "10px",
                    width: '100%',
                    height: '50px',
                    "& input": {
                      padding: "0.375rem 0.75rem",
                    },
                  }}
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                />
                <FormLabel
                  id="lastname-label"
                  sx={{
                    "& :focused": {
                      color: "#00000099",
                    }
                  }}
                >
                  Lastname
                </FormLabel>
                <TextField
                  sx={{
                    mt: "10px",
                    width: '100%',
                    height: '50px',
                    "& input": {
                      padding: "0.375rem 0.75rem",
                    },
                  }}
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                />
                <FormLabel
                  id="room-label"
                  sx={{
                    "& :focused": {
                      color: "#00000099",
                    }
                  }}
                >
                  Room Type
                </FormLabel>
                <Select
                  labelId="room-label"
                  fullWidth
                  required
                  value={rtype}
                  defaultValue={selectedReservation.room}
                  onChange={handleRTypeChange}
                  sx={{mt: "10px"}}
                >
                  {roomTypes.map((item, index) => (
                    <MenuItem value={item} key={index + Date.now()}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </FormControl>
          <FormControl sx={
            {
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              flexDirection: "column",
            }
          }>
            {loading && <CircularProgress sx={{mt: 3}}/>}
          </FormControl>
          <div className="modal-desc">
          </div>
          <div className="modal-footer">
            <button className="secondary-button" onClick={closeUpdate}>
              Cancel
            </button>
            <Button
              variant={"contained"}
              sx={{
                textTransform: "none",
              }}
              color="primary"
              onClick={updateReservations}
            >
              Update
            </Button>
          </div>
        </div>
      </Modal>
    </Paper>
  </>
}

export default CrewPopulated;
